/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { type ChangeEvent, forwardRef, type ReactNode, useRef } from 'react';

import classnames from 'classnames';

import useControlledState from '../../hooks/useControlledState';
import { useMergeRefs } from '../../hooks/useMergeRefs';
import { type InputBaseProps } from '../../types/formControls';
import { devWarn } from '../../utils';
import { FormControlMessage } from '../FormControlMessage';
import { Icon } from '../Icon';
import { Tooltip } from '../Tooltip';
import { Text } from '../Typography';

import styles from './Checkbox.module.scss';

export type CheckboxProps = InputBaseProps<{
    id?: string;
    /**
     * The content of the checkbox label.
     */
    children?: ReactNode;
    /**
     * If the checkbox is disabled.
     */
    disabled?: boolean;
    /**
     * Increased hit box for the checkbox. Useful for checkboxes within clickable rows.
     */
    increasedHitBoxSize?: boolean;
    /**
     * The indeterminate state of the checkbox.
     */
    indeterminate?: boolean;
    /**
     * Whether the checkbox is checked.
     */
    checked?: boolean;
    /**
     * Specifies the initial state: whether the checkbox is selected.
     */
    defaultChecked?: boolean;
    /** String to show a help message below the field. */
    helperText?: string;
    /**
     *  String to show a error message.
     */
    error?: string | boolean;
    /**
     * An informatory tooltip that is displayed when hovering over the label icon.
     */
    tooltip?: string;
    tooltipId?: string;
}>;

export type CheckboxChangeEvent = ChangeEvent<HTMLInputElement>;

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>((props: CheckboxProps, ref) => {
    const {
        id,
        children,
        checked,
        defaultChecked,
        disabled,
        onChange,
        increasedHitBoxSize,
        indeterminate,
        className,
        helperText,
        error,
        tooltip,
        tooltipId = '',
        ...checkboxProps
    } = props;
    const innerRef = useRef<HTMLInputElement>(null);
    const mergedRefs = useMergeRefs([innerRef, ref]);

    const [isChecked, setIsChecked] = useControlledState(
        checked,
        defaultChecked,
        'Checkbox',
        'checked'
    );

    const isControlled = 'checked' in props;

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (disabled) {
            return;
        }
        setIsChecked(e.target.checked);
        onChange?.(e);
    };

    if (process.env.NODE_ENV !== 'production') {
        if (indeterminate && !isControlled) {
            devWarn('The indeterminate prop should not be used without the checked prop.');
        }
    }

    return (
        <div className={styles.container}>
            <label
                className={classnames(styles.labelContainer, className, {
                    [styles.disabled]: disabled,
                    [styles.checked]: isChecked,
                    [styles.error]: error,
                    [styles.indeterminate]: indeterminate,
                    [styles.increasedHitBoxSize]: increasedHitBoxSize
                })}
            >
                <span className={classnames(styles.wrapper, { [styles.standalone]: !children })}>
                    <input
                        id={id}
                        ref={mergedRefs}
                        type="checkbox"
                        checked={!!isChecked}
                        disabled={Boolean(disabled)}
                        onChange={handleChange}
                        aria-checked={indeterminate ? 'mixed' : !!isChecked}
                        {...checkboxProps}
                    />
                    <span className={styles.inner}>
                        <Icon
                            name="s-icon-check-compact"
                            size="medium"
                            className={styles.checkmark}
                            aria-hidden
                        />
                        <div className={styles.indeterminateCheckmark} aria-hidden />
                    </span>
                </span>
                {children && <Text className={styles.label}>{children}</Text>}
                {tooltip && (
                    <Tooltip trigger={['hover', 'focus']} title={tooltip} id={tooltipId}>
                        <Icon
                            data-testid={`${tooltipId}-trigger`}
                            className={styles.icon}
                            name="s-icon-circle-info"
                            tabIndex={0}
                        />
                    </Tooltip>
                )}
            </label>
            {helperText && (
                <FormControlMessage
                    className={styles.helpText}
                    message={helperText}
                    type="helper"
                />
            )}
            {error && typeof error !== 'boolean' && <FormControlMessage message={error} />}
        </div>
    );
});

if (process.env.NODE_ENV !== 'production') {
    Checkbox.displayName = 'Checkbox';
}
